import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Container, Typography, Button, IconButton,
    TextField, InputAdornment, Divider, Chip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    ArrowBackOutlined, CheckCircleOutline,
    NorthEastOutlined, LockOutlined, InfoOutlined,
    StarRounded, StarBorderRounded,
} from '@mui/icons-material';
import {
    AreaChart, Area, XAxis, YAxis, Tooltip,
    ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { B, MUTUAL_FUNDS } from './investData';

/* ─── Custom tooltip ───────────────────────────────────────────── */
const DarkTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    return (
        <Box sx={{ background: B.dark, border: `1px solid ${alpha('#fff', .08)}`, borderRadius: '10px', px: 1.5, py: 1, boxShadow: `0 8px 24px ${alpha(B.dark, .5)}` }}>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '.8rem' }}>₦{payload[0].value.toFixed(2)}</Typography>
        </Box>
    );
};

const fmt = (n: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(n);

/* ─── Component ─────────────────────────────────────────────────── */
const MutualFundDetail: React.FC = () => {
    const { id }   = useParams<{ id: string }>();
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const fund = MUTUAL_FUNDS.find(f => f.id === id);

    const [step, setStep]       = useState<'detail' | 'amount' | 'confirm' | 'success'>('detail');
    const [amount, setAmount]   = useState('');
    const [period, setPeriod]   = useState<'1M' | '3M' | '6M' | '1Y' | 'ALL'>('1Y');

    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);
    const glass      = { background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none' };

    if (!fund) {
        return (
            <Box sx={{ minHeight: '100vh', background: pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ color: textMid }}>Fund not found.</Typography>
            </Box>
        );
    }

    const chartData = fund.chart.map((v, i) => ({ i: `W${i + 1}`, v }));
    const numAmount = parseFloat(amount.replace(/,/g, '')) || 0;
    const estimatedUnits = fund.nav > 0 ? numAmount / fund.nav : 0;
    const projectedYr1   = numAmount * (1 + fund.returns.yr1 / 100);

    /* ── Success screen ── */
    if (step === 'success') {
        return (
            <Box sx={{ minHeight: '100vh', background: pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                <Box sx={{ textAlign: 'center', px: 4 }}>
                    <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: alpha(B.green, .12), border: `2px solid ${alpha(B.green, .3)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                        <CheckCircleOutline sx={{ color: B.green, fontSize: 40 }} />
                    </Box>
                    <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.6rem', mb: .75 }}>Investment placed!</Typography>
                    <Typography sx={{ color: textMid, fontSize: '.85rem', mb: .5 }}>
                        {fmt(numAmount)} invested in
                    </Typography>
                    <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.9rem', mb: 3 }}>{fund.name}</Typography>
                    <Typography sx={{ color: textFaint, fontSize: '.78rem', mb: 4 }}>
                        Estimated units: {estimatedUnits.toFixed(4)} · Processing within 24hrs
                    </Typography>
                    <Button fullWidth variant="contained"
                            onClick={() => navigate('/invest?tab=funds')}
                            sx={{ background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, borderRadius: '14px', textTransform: 'none', fontWeight: 700, py: 1.5, boxShadow: 'none' }}>
                        Back to Investments
                    </Button>
                    <Button fullWidth onClick={() => { setStep('detail'); setAmount(''); }}
                            sx={{ mt: 1, color: textMid, textTransform: 'none', fontWeight: 600 }}>
                        Invest Again
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 12, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ══════════════════════════════════════════════ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${alpha(fund.color, .7)} 100%)`,
                mt: { xs: '-76px', sm: 0 }, pt: { xs: '80px', sm: '28px' }, pb: { xs: 4, sm: 14 },
                px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -60, right: -40, width: 220, height: 220, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(fund.color, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                    <IconButton size="small" onClick={() => step === 'detail' ? navigate('/invest?tab=funds') : setStep('detail')}
                                sx={{ color: alpha('#fff', .6), background: alpha('#fff', .08), '&:hover': { background: alpha('#fff', .16) } }}>
                        <ArrowBackOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Box flex={1}>
                        <Typography sx={{ color: alpha('#fff', .4), fontSize: '.62rem', letterSpacing: '.12em', textTransform: 'uppercase' }}>
                            {fund.category}
                        </Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '.95rem', letterSpacing: '-.02em' }}>
                            {fund.manager}
                        </Typography>
                    </Box>
                    <Box sx={{ px: 1.25, py: .4, borderRadius: 8, background: alpha(fund.riskColor, .18), border: `1px solid ${alpha(fund.riskColor, .3)}` }}>
                        <Typography sx={{ color: fund.riskColor, fontSize: '.65rem', fontWeight: 700 }}>{fund.risk} Risk</Typography>
                    </Box>
                </Box>

                <Typography sx={{ color: '#fff', fontFamily: "'Instrument Serif', serif", fontSize: '1.35rem', lineHeight: 1.3, mb: .5 }}>
                    {fund.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: .4, background: alpha(B.green, .14), border: `1px solid ${alpha(B.green, .25)}`, borderRadius: 20, px: 1.25, py: .4 }}>
                        <NorthEastOutlined sx={{ color: B.green, fontSize: 12 }} />
                        <Typography sx={{ color: B.green, fontWeight: 700, fontSize: '.72rem' }}>{fund.returns.yr1}% p.a.</Typography>
                    </Box>
                    <Typography sx={{ color: alpha('#fff', .35), fontSize: '.7rem' }}>AUM {fund.aum}</Typography>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ════════════════════════════════════════════
                    DETAIL VIEW
                ════════════════════════════════════════════ */}
                {step === 'detail' && (
                    <>
                        {/* Returns grid */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mb: 2 }}>
                            {[
                                { label: '1 Day',  value: `+${fund.returns.d1}%`  },
                                { label: 'YTD',    value: `+${fund.returns.ytd}%` },
                                { label: '1 Year', value: `+${fund.returns.yr1}%` },
                                { label: '3 Year', value: `+${fund.returns.yr3}%` },
                            ].map(r => (
                                <Box key={r.label} sx={{ ...glass, p: 1.5, textAlign: 'center' }}>
                                    <Typography sx={{ color: B.green, fontWeight: 700, fontSize: '.82rem' }}>{r.value}</Typography>
                                    <Typography sx={{ color: textFaint, fontSize: '.58rem', mt: .2 }}>{r.label}</Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Chart */}
                        <Box sx={{ ...glass, p: 2.5, mb: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.88rem' }}>Performance</Typography>
                                <Box sx={{ display: 'flex', gap: .5 }}>
                                    {(['1M', '3M', '6M', '1Y', 'ALL'] as const).map(p => (
                                        <Box key={p} onClick={() => setPeriod(p)} sx={{
                                            px: 1, py: .35, borderRadius: 8, cursor: 'pointer',
                                            background: period === p ? `linear-gradient(135deg, ${B.purple}, ${B.light})` : 'transparent',
                                            border: `1px solid ${period === p ? 'transparent' : alpha(isDark ? '#fff' : B.dark, .1)}`,
                                        }}>
                                            <Typography sx={{ color: period === p ? '#fff' : textFaint, fontSize: '.62rem', fontWeight: 700 }}>{p}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <ResponsiveContainer width="100%" height={160}>
                                <AreaChart data={chartData}>
                                    <defs>
                                        <linearGradient id={`grad-${fund.id}`} x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%"  stopColor={fund.color} stopOpacity={0.25} />
                                            <stop offset="95%" stopColor={fund.color} stopOpacity={0}    />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(isDark ? '#fff' : B.dark, .05)} vertical={false} />
                                    <XAxis dataKey="i" tick={{ fill: textFaint, fontSize: 9 }} axisLine={false} tickLine={false} />
                                    <YAxis domain={['auto', 'auto']} tick={{ fill: textFaint, fontSize: 9 }} axisLine={false} tickLine={false} width={32} />
                                    <Tooltip content={<DarkTooltip />} />
                                    <Area type="monotone" dataKey="v" stroke={fund.color} strokeWidth={2} fill={`url(#grad-${fund.id})`} dot={false} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Box>

                        {/* About */}
                        <Box sx={{ ...glass, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.88rem', mb: 1.25 }}>About this fund</Typography>
                            <Typography sx={{ color: textMid, fontSize: '.8rem', lineHeight: 1.7 }}>{fund.description}</Typography>
                            <Divider sx={{ my: 2, borderColor: dividerC }} />
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .75 }}>
                                {fund.highlights.map(h => (
                                    <Box key={h} sx={{ display: 'flex', alignItems: 'center', gap: .5, background: alpha(fund.color, .08), border: `1px solid ${alpha(fund.color, .18)}`, borderRadius: 20, px: 1.25, py: .5 }}>
                                        <CheckCircleOutline sx={{ fontSize: 12, color: fund.color }} />
                                        <Typography sx={{ color: fund.color, fontSize: '.68rem', fontWeight: 600 }}>{h}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Key stats */}
                        <Box sx={{ ...glass, p: 2.5, mb: 3 }}>
                            <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.88rem', mb: 1.75 }}>Key Details</Typography>
                            {[
                                { label: 'Fund Manager',   value: fund.manager                   },
                                { label: 'Category',       value: fund.category                  },
                                { label: 'Risk Level',     value: fund.risk                      },
                                { label: 'Min. Investment', value: fmt(fund.minInvestment)        },
                                { label: 'NAV per unit',   value: `₦${fund.nav.toFixed(2)}`      },
                                { label: 'Fund Size',      value: fund.aum                       },
                                { label: 'Liquidity',      value: 'T+1 Redemption'               },
                                { label: 'Regulator',      value: 'SEC Nigeria'                  },
                            ].map(({ label, value }) => (
                                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.1, borderBottom: `1px solid ${dividerC}`, '&:last-child': { borderBottom: 'none' } }}>
                                    <Typography sx={{ color: textMid, fontSize: '.78rem' }}>{label}</Typography>
                                    <Typography sx={{ color: textPri, fontWeight: 600, fontSize: '.78rem' }}>{value}</Typography>
                                </Box>
                            ))}
                        </Box>

                        <Button fullWidth variant="contained"
                                onClick={() => setStep('amount')}
                                sx={{ background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem', py: 1.6, boxShadow: 'none', '&:hover': { boxShadow: `0 8px 24px ${alpha(B.purple, .4)}` } }}>
                            Invest Now
                        </Button>
                    </>
                )}

                {/* ════════════════════════════════════════════
                    AMOUNT STEP
                ════════════════════════════════════════════ */}
                {step === 'amount' && (
                    <>
                        <Box sx={{ ...glass, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.68rem', letterSpacing: '.12em', textTransform: 'uppercase', mb: .5 }}>Investing in</Typography>
                            <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.9rem', mb: 2 }}>{fund.name}</Typography>

                            <Typography sx={{ color: textMid, fontSize: '.78rem', mb: 1.5 }}>Enter amount (NGN)</Typography>

                            <TextField
                                fullWidth
                                placeholder={`Min. ₦${fund.minInvestment.toLocaleString()}`}
                                value={amount}
                                onChange={e => {
                                    const raw = e.target.value.replace(/[^0-9]/g, '');
                                    setAmount(raw ? parseInt(raw).toLocaleString() : '');
                                }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Typography sx={{ color: textMid, fontWeight: 700 }}>₦</Typography></InputAdornment>,
                                    sx: {
                                        color: textPri, fontWeight: 700, fontSize: '1.2rem',
                                        borderBottom: `2px solid ${amount ? fund.color : alpha(isDark ? '#fff' : B.dark, .15)}`,
                                        '&:before, &:after': { display: 'none' },
                                    },
                                }}
                                variant="standard"
                                sx={{ mb: 2.5 }}
                            />

                            {/* Quick amounts */}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {[10000, 25000, 50000, 100000].map(q => (
                                    <Box key={q} onClick={() => setAmount(q.toLocaleString())} sx={{
                                        px: 1.5, py: .6, borderRadius: 20, cursor: 'pointer',
                                        background: numAmount === q ? alpha(fund.color, .12) : alpha(isDark ? '#fff' : B.dark, .05),
                                        border: `1px solid ${numAmount === q ? alpha(fund.color, .3) : alpha(isDark ? '#fff' : B.dark, .1)}`,
                                        transition: 'all .15s',
                                    }}>
                                        <Typography sx={{ color: numAmount === q ? fund.color : textMid, fontSize: '.72rem', fontWeight: 700 }}>
                                            ₦{(q / 1000).toFixed(0)}k
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Projection */}
                        {numAmount >= fund.minInvestment && (
                            <Box sx={{ ...glass, p: 2.5, mb: 2, border: `1px solid ${alpha(fund.color, .18)}` }}>
                                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.85rem', mb: 1.5 }}>Projected Returns</Typography>
                                {[
                                    { label: 'Units to receive',    value: `${estimatedUnits.toFixed(4)} units`   },
                                    { label: '1-year projection',   value: fmt(projectedYr1)                        },
                                    { label: 'Expected gain',       value: `+${fmt(projectedYr1 - numAmount)}`      },
                                ].map(({ label, value }) => (
                                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: .85, borderBottom: `1px solid ${dividerC}`, '&:last-child': { borderBottom: 'none' } }}>
                                        <Typography sx={{ color: textMid, fontSize: '.78rem' }}>{label}</Typography>
                                        <Typography sx={{ color: label.includes('gain') ? B.green : textPri, fontWeight: 700, fontSize: '.78rem' }}>{value}</Typography>
                                    </Box>
                                ))}
                                <Typography sx={{ color: textFaint, fontSize: '.62rem', mt: 1.25 }}>
                                    Based on {fund.returns.yr1}% historical 1-year returns. Not a guarantee.
                                </Typography>
                            </Box>
                        )}

                        <Button fullWidth variant="contained"
                                disabled={numAmount < fund.minInvestment}
                                onClick={() => setStep('confirm')}
                                sx={{ background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem', py: 1.6, boxShadow: 'none', '&:disabled': { background: alpha(B.mid, .4), color: alpha('#fff', .3) }, '&:hover:not(:disabled)': { boxShadow: `0 8px 24px ${alpha(B.purple, .4)}` } }}>
                            Continue
                        </Button>
                    </>
                )}

                {/* ════════════════════════════════════════════
                    CONFIRM STEP
                ════════════════════════════════════════════ */}
                {step === 'confirm' && (
                    <>
                        <Box sx={{ ...glass, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.88rem', mb: 2 }}>Confirm Investment</Typography>

                            {[
                                { label: 'Fund',            value: fund.name              },
                                { label: 'Amount',          value: fmt(numAmount)         },
                                { label: 'Units',           value: estimatedUnits.toFixed(4) },
                                { label: 'NAV per unit',    value: `₦${fund.nav}`         },
                                { label: 'Fund Manager',    value: fund.manager           },
                                { label: 'Processing',      value: 'Within 24 hours'      },
                                { label: 'Debit from',      value: 'NG Credit Wallet'     },
                            ].map(({ label, value }) => (
                                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.1, borderBottom: `1px solid ${dividerC}`, '&:last-child': { borderBottom: 'none' } }}>
                                    <Typography sx={{ color: textMid, fontSize: '.78rem' }}>{label}</Typography>
                                    <Typography sx={{ color: textPri, fontWeight: 600, fontSize: '.78rem', maxWidth: '55%', textAlign: 'right' }}>{value}</Typography>
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 3, p: 1.75, borderRadius: '14px', background: alpha(B.orange, .06), border: `1px solid ${alpha(B.orange, .18)}` }}>
                            <InfoOutlined sx={{ color: B.orange, fontSize: 16, mt: .1, flexShrink: 0 }} />
                            <Typography sx={{ color: alpha(isDark ? '#fff' : B.ink, .55), fontSize: '.74rem', lineHeight: 1.6 }}>
                                By confirming, you authorise NG Credit to debit {fmt(numAmount)} from your wallet and invest in {fund.name}. Redemption requests are processed within T+1 business days.
                            </Typography>
                        </Box>

                        <Button fullWidth variant="contained"
                                onClick={() => setStep('success')}
                                sx={{ background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem', py: 1.6, boxShadow: 'none', mb: 1.5, '&:hover': { boxShadow: `0 8px 24px ${alpha(B.purple, .4)}` } }}>
                            Confirm & Invest
                        </Button>
                        <Button fullWidth onClick={() => setStep('amount')}
                                sx={{ color: textMid, textTransform: 'none', fontWeight: 600 }}>
                            Go Back
                        </Button>
                    </>
                )}

                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        SEC LICENSED · FUNDS HELD IN TRUST
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default MutualFundDetail;